import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';

import './hub-shell.css';

/**
 * The 결재함 (approval-hub) masthead, generalised so every feature screen reached from the
 * left navigation shares one definition instead of thirty near-copies. Left side carries the
 * back control, title and description; the right side is a single status pill. The back
 * control sits on the title line, ahead of the title text - the site name that used to run as
 * an eyebrow above the title now lives once in the top-bar search, not on every screen.
 */
export function HubHeader({
  title,
  titleId,
  description,
  pill,
  onBack,
  backLabel = '업무 홈',
}: {
  title: ReactNode;
  titleId?: string;
  description?: ReactNode;
  pill?: ReactNode;
  onBack?: () => void;
  backLabel?: string;
}) {
  return (
    <header className="kop-hub-header">
      <div className="kop-hub-top-row">
        <div className="kop-hub-title-row">
          {onBack && (
            <button type="button" className="kop-hub-back" aria-label={backLabel} onClick={onBack}>
              <ChevronLeft size={22} />
            </button>
          )}
          <h1 id={titleId}>{title}</h1>
        </div>
        {pill}
      </div>
      {description && (
        <p className={onBack ? 'kop-hub-description--with-back' : undefined}>{description}</p>
      )}
    </header>
  );
}

export interface HubPillProps {
  tone?: 'positive' | 'negative' | 'warning' | 'info' | 'neutral';
  children: ReactNode;
}

/** The right-hand masthead pill (`.ah-connection` in approval-hub), tone-tinted by state. */
export function HubPill({ tone = 'neutral', children }: HubPillProps) {
  return <span className={`kop-hub-pill kop-hub-pill--${tone}`}>{children}</span>;
}

export interface HubFilterOption<T extends string> {
  value: T;
  label: string;
}

/** The 결재함 filter/tab strip (`.ah-filters`), for any screen that filters or switches views. */
export function HubFilters<T extends string>({
  items,
  value,
  onChange,
  ariaLabel,
}: {
  items: readonly HubFilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
}) {
  return (
    <nav className="kop-hub-filters" aria-label={ariaLabel}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          aria-pressed={value === item.value}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
}

/** The 결재함 empty/dashed panel (`.ah-empty`), a single centred sentence. */
export function HubEmpty({ children }: { children: ReactNode }) {
  return (
    <div className="kop-hub-empty" role="status">
      {children}
    </div>
  );
}
